import { useEffect, useState } from 'react';

import { resolveActorsQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';

import { classifyActor, type ResolvedActor } from './actor';

/**
 * Names for a set of audit identifiers, fetched once and remembered.
 *
 * Module-level cache, deliberately. The same author created most rows on a page and the same
 * handful of accounts recur across every screen, so a per-component cache would refetch the same
 * two ids on every navigation. Nothing here is sensitive beyond what the page already shows, and
 * a stale name is not a problem worth a cache-busting strategy.
 */
const cache = new Map<string, ResolvedActor>();
/** Ids already asked about, so a miss is not re-requested on every mount. */
const attempted = new Set<string>();

let inFlight: Promise<void> | null = null;
let queued: string[] = [];

async function flush(): Promise<void> {
  const ids = queued.filter((id) => !attempted.has(id));
  queued = [];
  if (!ids.length) return;
  ids.forEach((id) => attempted.add(id));
  try {
    const res = await fetchGraphQLApiData(resolveActorsQuery(ids));
    for (const a of res?.data?.resolveActors ?? []) {
      if (a?.id) cache.set(a.id, a);
    }
  } catch {
    /*
     * A name is a courtesy. If the lookup fails the caller still renders the shortened id, which
     * is what it showed before this existed -- so a failure here is a missing improvement, never
     * a broken screen.
     */
  }
}

/**
 * @param values raw createdBy/updatedBy strings. Only the id-shaped ones cost a request; emails,
 *   system labels and plain text are classified without one.
 */
export function useActors(values: (string | null | undefined)[]): Map<string, ResolvedActor> {
  const wanted = values
    .map((v) => classifyActor(v)?.lookupId)
    .filter((id): id is string => !!id);
  // Stable across renders so the effect does not refire on a new array with the same contents.
  const key = [...new Set(wanted)].sort().join(',');
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!key) return;
    const ids = key.split(',').filter((id) => !attempted.has(id));
    if (!ids.length) return;
    let cancelled = false;
    queued.push(...ids);
    inFlight = (inFlight ?? Promise.resolve()).then(flush);
    inFlight.then(() => {
      // Re-render so the freshly cached names replace the shortened ids.
      if (!cancelled) setTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return cache;
}
